import { useEffect, useState } from 'react';

import { Stack, Typography } from '@mui/material';
import Button from '@mui/material/Button';
import AddIcon from '@mui/icons-material/Add';
import Box from '@mui/material/Box';
import MainCard from '@components/ui/cards/MainCard';
import MainTable from '@components/MainTable';
import MainTablePagination from '@components/MainTable/MainTablePagination';
import { useTanStackTable } from '@hooks/useTanStackTable';
import useToast from '@hooks/useToast';
import useDataFetcher from '@hooks/useDataFetcher/useDataFetcher';
import {
  deleteUseCaseGroup,
  updateUseCaseGroupOrder,
  UseCaseGroup,
} from '@api/use-case-group';
import UseCaseGroupModal from '@features/admin-use-case-group/components/UseCaseGroupModal';
import { urls } from '@api/urls';
import useConfirmationModal from '@shared/hooks/useConfirmationModal';
import { useCaseGroupTableColumns } from './columns';

function UseCaseGroupTable() {
  const { showSuccess, showError } = useToast();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<UseCaseGroup | null>(null);
  const { showDeleteConfirmation, closeModal } = useConfirmationModal();

  const { data, isLoading, mutate } = useDataFetcher<UseCaseGroup[]>({
    url: urls.useCaseGroup.base,
  });

  const handleCreate = () => {
    setSelectedGroup(null);
    setIsModalOpen(true);
  };

  const handleDelete = async (group: UseCaseGroup) => {
    try {
      await deleteUseCaseGroup(group.id);
      showSuccess(
        'Success',
        `Use Case Group "${group.name}" has been deleted successfully`
      );
      mutate();
      closeModal();
    } catch (error) {
      showError('Error', 'Failed to delete Use Case Group');
      closeModal();
    }
  };

  const handleEdit = (group: UseCaseGroup) => {
    setSelectedGroup(group);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedGroup(null);
  };

  const handleModalSuccess = () => {
    mutate();
  };

  const { table, setData } = useTanStackTable<UseCaseGroup>({
    tableData: [],
    columnConfig: useCaseGroupTableColumns,
    options: {
      meta: {
        handleDeleteRow: (group: UseCaseGroup) => {
          showDeleteConfirmation(
            () => handleDelete(group),
            `Are you sure you want to delete "${group.name}"? This action cannot be undone.`
          );
        },
        handleEditRow: handleEdit,
        handleMoveUp: async (group: UseCaseGroup) => {
          await updateUseCaseGroupOrder(group.id, 'up');
          mutate();
        },
        handleMoveDown: async (group: UseCaseGroup) => {
          await updateUseCaseGroupOrder(group.id, 'down');
          mutate();
        },
      } as any,
    },
  });

  useEffect(() => {
    if (data) {
      setData([...data].sort((a, b) => a.order - b.order));
    }
  }, [data, setData]);

  return (
    <>
      <MainCard>
        <Stack gap={2}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h3">List of Use Case Groups</Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleCreate}
              sx={{
                flexGrow: { xs: 1, sm: 0 },
              }}
            >
              Create Use Case Group
            </Button>
          </Stack>
          <Box>
            <MainTable table={table} isLoading={isLoading} />
            <MainTablePagination table={table} isLoading={isLoading} />
          </Box>
        </Stack>
      </MainCard>

      <UseCaseGroupModal
        open={isModalOpen}
        onClose={handleModalClose}
        onSuccess={handleModalSuccess}
        group={selectedGroup}
      />
    </>
  );
}

export default UseCaseGroupTable;
